'use client';
import { Tab } from '@headlessui/react';
import { Typography } from 'components';
import { cn } from 'utils/cn';

import { useRouter } from 'next/navigation';
import { FC, HTMLAttributes, PropsWithChildren, ReactNode, useEffect } from 'react';

interface ITabToggle extends Omit<PropsWithChildren<HTMLAttributes<HTMLElement>>, 'onClick'> {
  /**
   * key
   */
  index: number;
  /**
   * Label contents
   */
  label: ReactNode;
  /**
   * Button disabled
   */
  disabled?: boolean;
  /**
   * Optional click handler
   */
  onClick?: (index?: number) => void;
  /**
   * Optional href
   */
  href?: string;
}

const TabCustomToggle: FC<ITabToggle> = ({ index, label, onClick, href = '', ...rest }) => {
  const router = useRouter();

  const handleClick = () => {
    if (href) return router.push(href);

    if (onClick) return onClick(index);
  };

  useEffect(() => {
    if (href) router.prefetch(href);
  }, [href, router]);

  return (
    <Tab
      className={cn('!m-0 w-full focus:outline-none active:outline-none')}
      key={index}
      onClick={handleClick}
      {...rest}
    >
      {({ selected }) => (
        <div
          className={cn(
            'flex flex-col',
            'mb-0 rounded-md border-b-4 border-transparent focus:outline-none active:outline-none ',
            'min-w-fit transition-colors duration-500 ease-in-out',
            selected ? ' bg-primary-blue-100' : 'border-transparent'
          )}
        >
          <div className="flex flex-col">
            <Typography
              variant="medium3"
              className={cn(
                selected ? `!text-white` : '!text-tertiary-200  ',
                '!m-0 inline-block h-full max-w-full truncate pt-1',
                'flex items-center justify-center gap-2'
              )}
            >
              {label}
            </Typography>
          </div>
        </div>
      )}
    </Tab>
  );
};

export default TabCustomToggle;
